import { useQuery } from '@tanstack/react-query';
import {
  SonarCloudComponentMeasure,
  SonarCloudProject,
} from '@internal/backstage-plugin-bidone-backend';
import { handleReponse } from '../helper';
import { UseQueryCustomResult } from '../types';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { REPO_NOT_IMPLEMENTED } from './useSonarQubeEntity';

const fetchSonarCloudProjects = async (
  projectsCsv: string,
  backendUrl: string,
  accessToken?: string,
): Promise<SonarCloudProject[]> => {
  const response = await fetch(
    `${backendUrl}/api/bidone/sonarcloud/projects?filter=${projectsCsv}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    },
  );

  return handleReponse(response);
};

const fetchSonarProjectMetrics = async (
  projectId: string,
  metrics: string[],
  backendUrl: string,
  accessToken?: string,
): Promise<SonarCloudComponentMeasure[]> => {
  const response = await fetch(
    `${backendUrl}/api/bidone/sonarcloud/projects/${projectId}/metrics`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({ metrics }),
    },
  );

  return handleReponse(response);
};

const useSonarCloudProjects = (
  ...projects: string[]
): UseQueryCustomResult<SonarCloudProject[]> => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  projects?.sort((a, b) => a.localeCompare(b));
  const projectsCsv = projects?.join(',') || '';

  return useQuery({
    queryKey: ['useSonarCloud', 'useSonarCloudProjects', projectsCsv],
    queryFn: async () => {
      const { token } = await identityApi.getCredentials();
      return fetchSonarCloudProjects(projectsCsv, backendUrl, token);
    },
  });
};

const useSonarCloudProjectMetrics = (
  projectKey: string,
  metrics: string[],
): UseQueryCustomResult<SonarCloudComponentMeasure[]> => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  metrics.sort((a, b) => a.localeCompare(b));
  const metricsKey = metrics.join(',');

  return useQuery({
    queryKey: [
      'useSonarCloud',
      'useSonarCloudProjectMetrics',
      projectKey,
      metricsKey,
    ],
    queryFn: async () => {
      if (projectKey === REPO_NOT_IMPLEMENTED) {
        return [];
      }
      const { token } = await identityApi.getCredentials();
      return fetchSonarProjectMetrics(projectKey, metrics, backendUrl, token);
    },
  });
};

export { useSonarCloudProjects, useSonarCloudProjectMetrics };
